import React, { useEffect, useState } from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import './Navbar.css';
import { supabase } from '../supabase';
import ReservationPopup from './ReservationPopup';
import { ArrowBack, CalendarMonth, LocalOffer, Logout, Message, Plumbing, CleaningServices } from '@mui/icons-material';
import { Home } from 'lucide-react';

function Navbar({children, conversationID}) {
    const [showNavMenu, setShowNavMenu] = useState(false);
    const [reservationData, setReservationData] = useState({});
    const [showEntireReservation, setShowEntireReservation] = useState(false);
    const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 992);

    // Track window size for responsive behavior
    useEffect(() => {
        const handleResize = () => {
            setIsDesktop(window.innerWidth >= 992);
            if (window.innerWidth >= 992) {
                setShowNavMenu(true); // Always show menu on desktop
            }
        };
        
        window.addEventListener('resize', handleResize);
        handleResize(); // Initial check
        
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const signOut = async () => {
        console.log("Attempting to log out...");
        const startTime = Date.now(); // Capture start time
        try {
            const { error } = await supabase.auth.signOut();
    
            if (error) {
                console.error("❌ Logout error:", error.message);
            } else {
                const endTime = Date.now();
                console.log("✅ Logout successful");
                console.log(`🕒 Time taken: ${endTime - startTime}ms`);
    
                // Optional: Redirect user after logout
                window.location.href = "/login"; 
            }
        } catch (err) {
            console.error("🚨 Unexpected logout error:", err);
        }
    };

    // Determine active menu item based on current URL
    const isActive = (path) => {
        return window.location.pathname === path ? 'active' : '';
    };
    
    // Function to toggle nav menu on mobile
    const toggleNavMenu = () => {
        if (!isDesktop) {
            setShowNavMenu(!showNavMenu);
        }
    };

    async function getReservationDetails (conversationId) {
        try {
          const { data, error } = await supabase
          .from('conversations')
          .select(`
            id,
            listing_map_id,
            reservation_id,
            recipient_email,
            recipient_name,
            recipient_picture,
            reservations (
              id,
              listing_map_id,
              channel_name,
              channel_reservation_id,
              external_property_id,
              guest_portal_url,
              reservation_date,
              guest_first_name,
              guest_last_name,
              number_of_guests,
              adults,
              children,
              infants,
              pets,
              arrival_date,
              departure_date,
              nights,
              total_price,
              tax_amount,
              phone,
              stripe_guest_id,
              channel_commission_amount,
              cleaning_fee,
              security_deposit_fee,
              stripe_guest_id,
              status,
              payment_status,
              revenue_per_day,
              deposit_paid,
              early_check_in_paid,
              early_check_in_time,
              late_check_out_paid,
              late_check_out_time,
              eta,
              etd,
              checked_out,
              conversation_notes,
              guest_rating,
              conversation_summary
            ),
            listings (
              id,
              name,
              internal_listing_name,
              airbnb_listing_url,
              vrbo_listing_url
            )
          `)
          .eq('id', conversationId);
      
          if (error) throw error;
          
          console.log('Reservation details:', data[0]);
          setReservationData(data[0]);
          return data;
        } catch (error) {
          console.error('Error fetching conversation details:', error.message);
          return null;
        }
    };

    useEffect(() => {
        if (conversationID) {
            getReservationDetails(conversationID)
        }
    }, [conversationID])

    function formatDate(inputDate) {
        const date = new Date(inputDate);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }

    const formatTime = (hour) => {
        if (hour < 0 || hour > 23 || isNaN(hour)) return "Invalid hour";
        
        const period = hour >= 12 ? "PM" : "AM";
        const formattedHour = hour % 12 || 12; // Converts 0 to 12, 13 to 1, etc.
        
        return `${formattedHour}:00 ${period}`;
    };
    
    if (new URL(window.location.href).pathname == '/login') {
        return children;
    }
      
    return (
        <div className='NavbarContainer'>

            {conversationID && !showNavMenu ? (
                <div className='NavbarContainerMainRow' style={{height: isDesktop ? 'auto' : 90}}>
                    <div className='NavbarContainerRow' >
                        <div className='NavbarContainerRowLeft'>
                            <div className='NavbarContainerConversationBackButton' onClick={() => {window.location.href = '/conversations'}}>
                                <ArrowBack style={{color: '#000', width: '20px', height: '20px', marginRight: '10px'}} />
                            </div>
                            <img onClick={() => {setShowEntireReservation(!showEntireReservation)}} src={reservationData.recipient_picture} className='NavbarContainerConversationProfilePicture' />
                            <div onClick={() => {setShowEntireReservation(!showEntireReservation)}}>
                                <div className='NavbarContainerConversationProfileName'>{reservationData.recipient_name}</div>
                                <div className='NavbarContainerConversationProfileDates'>{reservationData.listings?.internal_listing_name} · {formatDate(reservationData.reservations?.arrival_date)}–{formatDate(reservationData.reservations?.departure_date)}</div>
                            </div>
                        </div>
                        <div className='NavbarContainerRowRight' onClick={toggleNavMenu}>
                            <MenuIcon />
                        </div>
                    </div>


                    <div className='NavbarConversationDetails'>
                        <div className='NavbarConversationDetailsTag'>
                            <div className='NavbarConversationDetailsTagDot' style={{backgroundColor: reservationData?.reservations?.payment_status == 'Paid' && '#00b000'}}></div>
                            <div className='NavbarConversationDetailsTagTitle'>Paid</div>
                        </div>
                        <div className='NavbarConversationDetailsTag'>
                            <div className='NavbarConversationDetailsTagDot' style={{backgroundColor: reservationData?.reservations?.stripe_guest_id && '#00b000'}}></div>
                            <div className='NavbarConversationDetailsTagTitle'>Card Added</div>
                        </div>

                        <div className='NavbarConversationDetailsTag'>
                            <div className='NavbarConversationDetailsTagDot' style={{backgroundColor: reservationData?.reservations?.stripe_guest_id && '#00b000'}}></div>
                            <div className='NavbarConversationDetailsTagTitle'>Deposit Paid</div>
                        </div>
                        {reservationData?.reservations?.early_check_in_paid && (
                        <div className='NavbarConversationDetailsTag'>
                            <div className='NavbarConversationDetailsTagDot' style={{backgroundColor: reservationData?.reservations?.early_check_in_paid && '#00b000'}}></div>
                            <div className='NavbarConversationDetailsTagTitle'>{formatTime(reservationData?.reservations?.early_check_in_time)} Arrival</div>
                        </div>
                        )}
                        {reservationData?.reservations?.eta && (
                        <div className='NavbarConversationDetailsTag'>
                            <div className='NavbarConversationDetailsTagDot' style={{backgroundColor: '#00b000'}}></div>
                            <div className='NavbarConversationDetailsTagTitle'>{formatTime(reservationData?.reservations?.eta)} ETA</div>
                        </div>
                        )}
                        {reservationData?.reservations?.etd && (
                        <div className='NavbarConversationDetailsTag'>
                            <div className='NavbarConversationDetailsTagDot' style={{backgroundColor: '#00b000'}}></div>
                            <div className='NavbarConversationDetailsTagTitle'>{formatTime(reservationData?.reservations?.etd)} ETD</div>
                        </div>
                        )}
                        {reservationData?.reservations?.checked_out && (
                        <div className='NavbarConversationDetailsTag'>
                            <div className='NavbarConversationDetailsTagDot' style={{backgroundColor: reservationData?.reservations?.checked_out && '#00b000'}}></div>
                            <div className='NavbarConversationDetailsTagTitle'>Departed</div>
                        </div>
                        )}
                        {reservationData?.reservations?.late_check_out_paid && (
                        <div className='NavbarConversationDetailsTag'>
                            <div className='NavbarConversationDetailsTagDot' style={{backgroundColor: reservationData?.reservations?.late_check_out_paid && '#00b000'}}></div>
                            <div className='NavbarConversationDetailsTagTitle'>{formatTime(reservationData?.reservations?.late_check_out_time)} Departure</div>
                        </div>
                        )}
                    </div>


                </div>
            ) : (
                <div className='NavbarContainerRow'>
                    <div className='NavbarContainerRowLeft'>
                        <div className='NavbarContainerRowLeftText'>S & L Escapes</div>
                    </div>
                    <div className='NavbarContainerRowRight' onClick={toggleNavMenu}>
                        <MenuIcon />
                    </div>
                    
                    {/* Navbar Menu - now includes condition for desktop display */}
                    <div className='NavbarMenu' style={{ height: showNavMenu ? (isDesktop ? 'auto' : 'calc(100vh - 50px)') : '0px', opacity: showNavMenu ? '1' : '0' }}>
                        <div className='NavbarMenuItems'>
                            <a className={`NavbarMenuItem ${isActive('/')}`} href='/'>
                                <Home style={{marginRight: '10px'}}/> Home
                            </a>
                            <a className={`NavbarMenuItem ${isActive('/reservations')}`} href='/reservations'>
                                <LocalOffer style={{marginRight: '10px'}}/> Reservations
                            </a>
                            <a className={`NavbarMenuItem ${isActive('/conversations')}`} href='/conversations'>
                                <Message style={{marginRight: '10px'}}/> Messages
                            </a>
                            <a className={`NavbarMenuItem ${isActive('/calendar')}`} href='/calendar'>
                                <CalendarMonth style={{marginRight: '10px'}}/> Calendar
                            </a>
                            <a className={`NavbarMenuItem ${isActive('/maintenance')}`} href='/maintenance'>
                                <Plumbing style={{marginRight: '10px'}}/> Maintenance
                            </a>
                            <a className={`NavbarMenuItem ${isActive('/cleaners')}`} href='/cleaners'>
                                <CleaningServices style={{marginRight: '10px'}}/> Cleaners
                            </a>
                            <a className='NavbarMenuItem' onClick={signOut} href='#'>
                                <Logout style={{marginRight: '10px'}}/> Logout
                            </a>
                        </div>
                    </div>
                </div>
            )}

            <ReservationPopup reservationData={reservationData} showEntireReservation={showEntireReservation} setShowEntireReservation={setShowEntireReservation} />

            {/* This is the mobile-only version of the menu */}
            {!isDesktop && (
                <div className='NavbarMenu' style={{ height: showNavMenu ? 'calc(100vh - 50px)' : '0px', opacity: showNavMenu ? '1' : '0' }}>
                    <div className='NavbarMenuItems'>
                        <a className={`NavbarMenuItem ${isActive('/')}`} href='/'>
                            <Home style={{marginRight: '10px'}}/> Home
                        </a>
                        <a className={`NavbarMenuItem ${isActive('/reservations')}`} href='/reservations'>
                            <LocalOffer style={{marginRight: '10px'}}/> Reservations
                        </a>
                        <a className={`NavbarMenuItem ${isActive('/conversations')}`} href='/conversations'>
                            <Message style={{marginRight: '10px'}}/> Messages
                        </a>
                        <a className={`NavbarMenuItem ${isActive('/calendar')}`} href='/calendar'>
                            <CalendarMonth style={{marginRight: '10px'}}/> Calendar
                        </a>
                        <a className={`NavbarMenuItem ${isActive('/maintenance')}`} href='/maintenance'>
                            <Plumbing style={{marginRight: '10px'}}/> Maintenance
                        </a>
                        <a className={`NavbarMenuItem ${isActive('/cleaners')}`} href='/cleaners'>
                            <CleaningServices style={{marginRight: '10px'}}/> Cleaners
                        </a>
                        <a className='NavbarMenuItem' onClick={signOut} href='#'>
                            <Logout style={{marginRight: '10px'}}/> Logout
                        </a>
                    </div>
                </div>
            )}

            {/* Main content */}
            <div style={{ marginTop: isDesktop ? '64px' : '0px' }}>
                {children}
            </div>
        </div>
    );
}

export default Navbar;
